import { Header } from "@/components/Header";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { CTASection } from "@/components/CTASection";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import { ServiceCard } from "@/components/ServiceCard";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import {
  masonryServices,
  pavingServices,
  concreteServices,
  waterproofingServices,
} from "@/data/servicesData";
import servicesHeroImage from "@/assets/services-hero-trucks.webp";

const Services = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Masonry & Concrete Services Totowa, NJ | Masonry Pro</title>
        <meta name="description" content="Masonry, paving, concrete, and waterproofing services from Masonry Pro Construction in Totowa, NJ, serving Bergen, Passaic, Essex, and Morris counties." />
        <meta property="og:title" content="Masonry & Concrete Services Totowa, NJ | Masonry Pro" />
        <meta property="og:description" content="Masonry, paving, concrete, and waterproofing services from Masonry Pro Construction in Totowa, NJ, serving Bergen, Passaic, Essex, and Morris counties." />
        <meta name="twitter:title" content="Masonry, Paving & Concrete Services NJ | Masonry Pro" />
        <meta name="twitter:description" content="Full-service masonry, paving, concrete, and waterproofing in North Jersey. Patios, walls, driveways, foundations, repairs. 35+ years. Free estimates." />
        <meta property="og:image" content="https://masonry-pro.com/og/services.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/services.jpg" />
      </Helmet>
      
      <Header />
      <Breadcrumb />
      
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
          <img src={servicesHeroImage} alt="Masonry, concrete, and paving services in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.55) 0%, rgba(13,16,23,0.75) 100%)' }} />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                Our Masonry, Paving, Concrete &amp; Waterproofing Services in <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Totowa, NJ</span>
              </h1>
              <div style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.125rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }} className="space-y-4">
                <p>
                  Looking for a <Link to="/pro-tips/choosing-a-professional-masonry-company" className="text-secondary hover:underline">masonry, paving, or concrete contractor</Link> in North Jersey? You're in the right place.
                </p>
                <p>
                  We handle everything from paver patios, walkways, and retaining walls to driveways, sidewalks, and foundations. We also install outdoor kitchens, fire pits, stone veneer, steps, and waterproofing for basements and patios. Some jobs are quick repairs. Others are full outdoor living spaces that take a few weeks. Either way, we show up, do the work right, and stand behind it. Our <Link to="/faq" className="text-secondary hover:underline">frequently asked questions</Link> answer the common ones before you call.
                </p>
                <p>
                  Take a look below and find what you need. If you're not sure which service fits your project, give us a call or text at{" "}
                  <a href="tel:+19737252395" className="text-secondary hover:underline font-semibold">
                    (973) 725-2395
                  </a>
                  . We'll help you figure it out.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Masonry Contractor Services */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="text-gradient-gold">Masonry</span> Contractor Services
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl mx-auto">
              From stone walls to outdoor living spaces, we build masonry that lasts. Every project starts with proper drainage and ends with craftsmanship you can see.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {masonryServices.map((service) => (
                <ServiceCard
                  key={service.title}
                  image={service.image}
                  title={service.title}
                  description={service.description}
                  link={service.link}
                  alt={service.alt}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Paving Contractor Services */}
        <section className="py-20 bg-card">
          <div className="container mx-auto px-4 text-center">
            <Link to="/paving-services" className="group/heading inline-block">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 group-hover/heading:text-secondary transition-colors">
                <span className="text-gradient-gold">Paving</span> Contractor Services
              </h2>
            </Link>
            <p className="text-muted-foreground mb-12 max-w-2xl mx-auto">
              Driveways, walkways, patios, and sidewalks. We install pavers and pour concrete with proper base preparation so your surfaces stay level for years.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {pavingServices.map((service) => (
                <ServiceCard
                  key={service.title}
                  image={service.image}
                  title={service.title}
                  description={service.description}
                  link={service.link}
                  alt={service.alt}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Concrete Contractor Services */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 text-center">
            <Link to="/concrete-services" className="group/heading inline-block">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 group-hover/heading:text-secondary transition-colors">
                <span className="text-gradient-gold">Concrete</span> Contractor Services
              </h2>
            </Link>
            <p className="text-muted-foreground mb-12 max-w-2xl mx-auto">
              Foundations, footings, and decorative concrete. We pour it right the first time with proper reinforcement and finishing.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {concreteServices.map((service) => (
                <ServiceCard
                  key={service.title}
                  image={service.image}
                  title={service.title}
                  description={service.description}
                  link={service.link}
                  alt={service.alt}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Waterproofing Services */}
        <section className="py-20 bg-card">
          <div className="container mx-auto px-4 text-center">
            <Link to="/waterproofing-services" className="group/heading inline-block">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 group-hover/heading:text-secondary transition-colors">
                <span className="text-gradient-gold">Waterproofing</span> Services
              </h2>
            </Link>
            <p className="text-muted-foreground mb-12 max-w-2xl mx-auto">
              Stop water problems before they cause damage. We waterproof basements, patios, and elevated structures to protect your investment.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
              {waterproofingServices.map((service) => (
                <ServiceCard
                  key={service.title}
                  image={service.image}
                  title={service.title}
                  description={service.description}
                  link={service.link}
                  alt={service.alt}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Project Packages entry point */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto rounded-lg border border-secondary/30 bg-card p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">
                  Looking for a <span className="text-gradient-gold">complete build?</span>
                </h2>
                <p className="text-muted-foreground">
                  Browse our bundled project packages: paver patios, outdoor kitchens, driveways, retaining walls, and more.
                </p>
              </div>
              <Button asChild variant="gold" size="lg" className="shrink-0">
                <Link to="/packages" className="inline-flex items-center gap-2">
                  View Project Packages
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      
      <Footer />
      <AccessibilityWidget />
    </div>
  );
};

export default Services;
